/**
 * JS masonry polyfill — heavily based on masonry-pf@1.0.0
 * @see https://github.com/Sam-Apostel/masonry-pf/tree/v1.0.0
 *
 * Overview:
 *   No shipping browser lays out `grid-template-rows: masonry` correctly: WebKit on
 *   iOS 16.x accepts the value (CSS.supports says true) but its experimental layout
 *   overlaps cards, so the grid never declares it and this class always runs. It
 *   places every card explicitly (grid row + column) and stacks the cards of a
 *   column with `margin-top` (see #place, #pickColumn). The same placement
 *   applies while organizing, so the list looks the same in both modes.
 *
 *   Sizes are read via offset* metrics (offsetTop/offsetHeight) rather than
 *   getBoundingClientRect, because offset* metrics are immune to CSS transforms —
 *   and transforms are in play here (Sortable animates reordering with them, and
 *   appended cards fade in with a scale transform); getBoundingClientRect would
 *   return wrong, mid-animation positions while a drag is in flight. The catch: a
 *   transformed element becomes the offsetParent of its descendants, which would
 *   move the reference frame of `content.offsetTop`. To keep content offsets
 *   always measured against their item regardless of any transform, this class
 *   forces `position: relative` on every item (see #elements) so the item is
 *   itself the offsetParent of its content.
 *
 * Section dividers (data-masonry-break):
 *   Full-span section headers each take a row of their own. Masonry is applied
 *   independently to each group of items between section dividers so that
 *   products never try to fill gaps across a section boundary.
 */
export class Masonry {
  constructor(grid) {
    this.grid = grid
    this.active = !!this.grid
    this.frameId = null
    // Chaque carte est posée à une hauteur calculée d'après la taille des cartes
    // au-dessus d'elle : si l'une d'elles change ensuite de taille (image réaffichée au
    // retour sur une page restaurée, cartes masquées puis réaffichées, WebView
    // redimensionnée), toute la grille est recalculée — sinon vide ou chevauchement.
    // La hauteur ayant servi au placement est écrite sur la carte (data-masonry-height) :
    // elle revient avec une page restaurée, où l'instance est neuve. Les placements y
    // reviennent aussi, et ne sont recalculés que si une carte n'a plus cette hauteur.
    this.observed = new WeakSet()
    this.resizeObserver = this.active && new ResizeObserver((entries) => {
      const changed = entries.some(({ target }) =>
        'masonryHeight' in target.dataset && Number(target.dataset.masonryHeight) !== target.offsetHeight)
      if (changed) this.layout()
    })
  }

  disconnect() {
    if (this.frameId) cancelAnimationFrame(this.frameId)
    this.resizeObserver?.disconnect()
  }

  // Called right after items are revealed (first render, infinite pagination): laid out
  // synchronously, in the same task as the reveal, so the browser never paints the
  // plain row-aligned grid before the masonry margins apply — that one frame read as
  // a visible jump when a list opened.
  // Seules les cartes ajoutées sont placées, à la suite de l'état laissé par le passage
  // précédent : les cartes déjà affichées ne bougent jamais. Recalculer toute la grille
  // déplaçait celles à l'écran au retour sur une liste restaurée depuis le cache Turbo
  // (l'app native y revient ainsi), ses images pas encore réaffichées étant mesurées
  // trop petites.
  appended() {
    if (!this.active) return

    if (this.frameId) cancelAnimationFrame(this.frameId)
    this.frameId = null
    if (!this.#layoutAppended()) this.#layoutAll()
  }

  // Immediate relayout, no frame coalescing. Also the right call while a card is being
  // dragged: Sortable fires `onChange` right after moving the card in the DOM and BEFORE
  // measuring where every card lands to animate it there — a relayout deferred to the
  // next frame (layout()) came after that measure, so the cards animated towards their
  // stale, un-compacted positions and overlapped until the drop.
  layoutNow() {
    if (!this.active) return

    if (this.frameId) cancelAnimationFrame(this.frameId)
    this.frameId = null
    this.#layoutAll()
  }

  // Full relayout — called on window resize or after drag-and-drop reorder.
  layout() {
    if (!this.active) return

    this.#scheduleLayout()
  }

  // Coalesce rapid calls (sortable:changed bursts, resize) into a single run per frame.
  #scheduleLayout() {
    if (this.frameId) cancelAnimationFrame(this.frameId)
    this.frameId = window.requestAnimationFrame(() => {
      this.frameId = null
      this.#layoutAll()
    })
  }

  #layoutAll() {
    const elements = this.#elements()

    // Écritures, puis lectures : la marge propre de la carte (-m-2) se lit sans la
    // valeur posée au passage précédent, et les colonnes se comptent sans aucun
    // placement — une carte restée en 4e colonne quand la grille passe à 3, ou toute
    // une section posée sur une même rangée, crée des colonnes implicites que le
    // décompte prendrait pour de vraies.
    elements.forEach(({ style }) => {
      style.removeProperty('margin-top')
      style.removeProperty('grid-row-start')
      style.removeProperty('grid-column-start')
    })
    const columnsLength = this.#columnsLength()
    if (columnsLength <= 1) {
      delete this.grid.dataset.masonryState
      return
    }

    this.#place(elements, { row: 1, bottoms: new Array(columnsLength).fill(null) })
  }

  // Places the cards added at the end of the grid, from the saved state. False when it
  // can't: no state yet, grid width changed, or new cards anywhere but at the end.
  #layoutAppended() {
    const state = this.grid.dataset.masonryState && JSON.parse(this.grid.dataset.masonryState)
    if (!state || state.width !== this.grid.clientWidth) return false

    const elements = this.#elements()
    const firstNew = elements.findIndex((el) => !('masonryPlaced' in el.dataset))
    if (firstNew < 0) return true

    const added = elements.slice(firstNew)
    if (added.some((el) => 'masonryPlaced' in el.dataset)) return false

    this.#place(added, state)
    return true
  }

  // Each group of cards (between section dividers) takes a single grid row: its
  // cards all sit in that row's cells, stacked in their column by margin-top. The
  // row grows to the tallest column, so the next divider lands right below the
  // group. Card heights don't depend on the column (all columns share a width):
  // everything is read in one pass, then written. `state` is where the previous
  // pass left off — the current group's row and the bottom of each of its columns —
  // and is saved on the grid for the next one (it survives the Turbo cache).
  #place(elements, state) {
    const gap = parseFloat(this.#computedStyle.rowGap) || 0
    const columnWidth = parseFloat(this.#computedStyle.gridTemplateColumns) || 0
    const tolerance = columnWidth * SHORTEST_COLUMN_TOLERANCE
    const width = this.grid.clientWidth
    const measures = elements.map((el) => {
      if (isDivider(el)) return null

      const content = el.firstElementChild
      return {
        height: el.offsetHeight,
        ownMargin: parseFloat(window.getComputedStyle(el).marginTop) || 0,
        contentBottom: content ? content.offsetTop + content.offsetHeight : el.offsetHeight,
      }
    })

    let { row } = state
    // Bas du contenu de chaque colonne, relatif au haut de la rangée (null : vide).
    let bottoms = [...state.bottoms]
    elements.forEach((el, index) => {
      el.dataset.masonryPlaced = ''
      if (!measures[index]) {
        if (bottoms.some((bottom) => bottom !== null)) row += 1
        el.style.gridRowStart = `${row}`
        row += 1
        bottoms = bottoms.map(() => null)
        return
      }

      const { height, ownMargin, contentBottom } = measures[index]
      el.dataset.masonryHeight = height
      const column = this.#pickColumn(bottoms, tolerance)
      const above = bottoms[column]
      const marginTop = above === null ? ownMargin : above + gap
      el.style.gridRowStart = `${row}`
      el.style.gridColumnStart = `${column + 1}`
      if (above !== null) el.style.setProperty('margin-top', `${marginTop}px`)
      bottoms[column] = marginTop + contentBottom
    })
    this.grid.dataset.masonryState = JSON.stringify({ width, row, bottoms })
  }

  // The list order fills the columns, each card going to the shortest one, so the
  // list order reads top to bottom — but only when it is clearly shorter: every
  // column ending within `tolerance` of the shortest counts as level, and the leftmost of those wins. Without it, a column
  // a few pixels shorter on the far right drew the next card there, which read as
  // a misplaced card; with it, a column falling behind is filled before it leaves
  // a hole. An empty column always comes first, from the left.
  #pickColumn(bottoms, tolerance) {
    const empty = bottoms.indexOf(null)
    if (empty >= 0) return empty

    const shortest = Math.min(...bottoms)
    return bottoms.findIndex((bottom) => bottom - shortest <= tolerance)
  }

  // The grid's visible cards and section dividers (rendered with a
  // `data-masonry-break` attribute), in order.
  #elements() {
    const elements = []
    for (const el of this.grid.children) {
      if (!isVisible(el)) continue
      // Sortable's touch-drag clone: fixed-positioned, following the finger, appended to
      // the grid. Not a card — counting it broke the column accounting, and forcing
      // `position: relative` on it pinned it in place under the finger.
      if (el.classList.contains('sortable-fallback')) continue
      // Force position:relative so each item is the offsetParent of its content
      // (see class note). The inline-style check skips the write once it's set
      // (reading style.position doesn't trigger a reflow).
      if (!isDivider(el) && el.style.position !== 'relative') el.style.position = 'relative'
      // Toutes les cartes d'un groupe partagent une rangée : étirées à sa hauteur (grille
      // sans items-start, celle des tendances), elles changeaient de taille à chaque
      // placement et relançaient l'observateur — un recalcul par image, en boucle.
      if (!isDivider(el) && el.style.alignSelf !== 'start') el.style.alignSelf = 'start'
      // Observer de nouveau une carte déjà suivie relance sa notification initiale : une
      // par carte de la grille à chaque page ajoutée.
      if (!isDivider(el) && !this.observed.has(el)) {
        this.observed.add(el)
        this.resizeObserver.observe(el)
      }
      elements.push(el)
    }
    return elements
  }

  #columnsLength() {
    return this.#computedStyle.gridTemplateColumns.split(' ').length
  }

  get #computedStyle() {
    return window.getComputedStyle(this.grid)
  }
}

// Columns ending within this share of a column's width of the shortest one count as
// level (see #pickColumn).
const SHORTEST_COLUMN_TOLERANCE = 0.5

function isDivider(element) {
  return 'masonryBreak' in element.dataset
}

// offsetParent is null for elements with display: none.
function isVisible(element) {
  return element.offsetParent !== null
}
